// src/utils/rankUtils.ts

import {PlayerStats} from "../types";
import {rankOnLadder, tierOfPosition} from "../data/rankLadder";

export const getRankImagePath = (rank: string | null): string | null => {
    if (!rank) return null;

    // Map ranks to their corresponding image files in public/images folder
    const rankImageMap: Record<string, string> = {
        'nooreminspektor': '/images/nooreminspektor.png',
        'inspektor': '/images/inspektor.png',
        'vaneminspektor': '/images/vaneminspektor.png',
        'üleminspektor': '/images/yleminspektor.png',
        'komissar': '/images/komissar.png',
        'vanemkomissar': '/images/vanemkomissar.png',
        'politseileitnant': '/images/politseileitnant.png',
        'politseikapten': '/images/politseikapten.png',
        'politseimajor': '/images/politseimajor.png',
        'politseikolonelleitnant': '/images/politseikolonelleitnant.png',
        // The prefekt's three (55.90 ranks, insignia 55.93).
        'politseikolonel': '/images/politseikolonel.png',
        'politseikindralinspektor': '/images/politseikindralinspektor.png',
        'politseikindral': '/images/politseikindral.png'
    };

    // Convert to lowercase for case-insensitive matching
    const normalizedRank = rank.toLowerCase();
    return rankImageMap[normalizedRank] || null;
};

/**
 * The ladder, lowest first. Only used to compare two ranks.
 *
 * Mirrors RANK_ORDER in functions/src/rank.ts, which is the authority — this copy exists so
 * the UI does not propose a change the server would refuse.
 */
const RANK_ORDER = [
    'nooreminspektor',
    'inspektor', 'vaneminspektor', 'üleminspektor', 'komissar', 'vanemkomissar',
    'politseileitnant', 'politseikapten', 'politseimajor', 'politseikolonelleitnant',
    'politseikolonel', 'politseikindralinspektor', 'politseikindral',
];

const rankIndex = (rank: string | null): number => (rank ? RANK_ORDER.indexOf(rank.toLowerCase()) : -1);

/** The higher of what the player has earned and what their current job would give. */
export const highestRank = (stored: string | null, derived: string | null): string | null =>
    rankIndex(derived) > rankIndex(stored) ? derived : stored;

/**
 * The rank the player's CURRENT job and level would grant, ignoring what they have earned.
 *
 * Rarely what you want on its own: a rank is earned and kept, so a player who steps down from
 * a leadership post keeps the rank it gave them. Use `checkRankUpdate`, which applies that.
 */
export const getCorrectRank = (playerStats: PlayerStats): string | null => {
    const hasGraduated = playerStats.completedCourses?.includes('lopueksam') || false;
    if (!hasGraduated) return null;

    // RANKS.md "Promotion inside a tier" (55.90), from the one table (data/rankLadder.ts): the job sets the rank
    // on entry, the level promotes to the rung's ceiling. A graduate is always an officer at least, as deriveRank has it.
    const tier = tierOfPosition(playerStats.policePosition);
    const rung = tier === null || tier === 'abipolitseinik' || tier === 'kadett' ? 'officer' : tier;
    return rankOnLadder(rung, playerStats.level || 1);
};

/**
 * Checks if player rank needs updating and returns the correct rank
 * @param playerStats - Current player stats
 * @returns New rank if update needed, null if current rank is correct
 */
export const checkRankUpdate = (playerStats: PlayerStats): string | null => {
    // Never below what is already held: a position raises a rank, it does not lower one.
    const correctRank = highestRank(playerStats.rank ?? null, getCorrectRank(playerStats));

    // If the correct rank is different from current rank, return the new rank
    if (correctRank !== playerStats.rank) {
        return correctRank;
    }

    return null; // No update needed
};
